/**
 * useDocumentMutations Hook
 *
 * Provides mutation handlers for document blocks with API persistence.
 * - Section mutations (create, update, delete, reorder)
 * - Document mutations (update name, description)
 * - Play reference mutations (add, remove)
 * - Operation-based undo/redo support
 *
 * Uses optimistic updates for instant UI feedback with rollback on error.
 * Follows the presentation editor pattern for undo/redo.
 */

import { useCallback, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { secureJsonFetch, secureFetch } from '@/utils/SecureFetch'
import { getResolvedDocumentQueryKey } from '@/hooks/UseResolvedDocument'
import type { SectionBlock, DocumentBlock, PlayReferenceBlock, ResolvedDocument } from '@/types/blocks'

export interface CreateSectionData {
	name: string
	color?: string
}

export interface UpdateSectionData {
	name?: string
	color?: string
	collapsed?: boolean
}

export interface UpdateDocumentData {
	name?: string
	description?: string | null
}

// ========== Undo/Redo Types ==========

type DocumentMutationType =
	| 'create_section'
	| 'delete_section'
	| 'update_section'
	| 'update_document'
	| 'add_play_reference'
	| 'remove_play_reference'
	| 'reorder_sections'

interface UndoableOperation {
	id: string
	type: DocumentMutationType
	label: string
	/** Data needed to reverse the operation */
	inverseData: Record<string, unknown>
	/** Data needed to redo the operation */
	redoData: Record<string, unknown>
}

function generateOperationId(): string {
	return `op_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
}

const MAX_UNDO_LEVELS = 50

export interface DocumentMutations {
	createSection: (data: CreateSectionData) => Promise<SectionBlock>
	updateSection: (sectionId: string, data: UpdateSectionData) => Promise<SectionBlock>
	deleteSection: (sectionId: string) => Promise<void>
	updateDocument: (data: UpdateDocumentData) => Promise<DocumentBlock>
	addPlayReference: (sectionId: string, playId: string) => Promise<PlayReferenceBlock>
	removePlayReference: (blockId: string) => Promise<void>
	reorderSections: (sectionIds: string[]) => Promise<void>
	undo: () => Promise<void>
	redo: () => Promise<void>
	canUndo: boolean
	canRedo: boolean
}

function getApiBaseUrl(): string {
	if (typeof window !== 'undefined' && window.location?.origin) {
		return window.location.origin
	}
	return ''
}

/**
 * Hook for document block mutations with API persistence.
 * Implements optimistic updates and operation-based undo/redo.
 *
 * @param documentId - The document block ID
 * @returns Mutation handlers with undo/redo support
 */
export function useDocumentMutations(documentId: string | null): DocumentMutations {
	const queryClient = useQueryClient()
	const baseUrl = getApiBaseUrl()

	// Undo/redo stacks (ref-based to avoid re-renders on every push)
	const undoStackRef = useRef<UndoableOperation[]>([])
	const redoStackRef = useRef<UndoableOperation[]>([])
	const [canUndo, setCanUndo] = useState(false)
	const [canRedo, setCanRedo] = useState(false)

	const updateUndoState = useCallback(() => {
		setCanUndo(undoStackRef.current.length > 0)
		setCanRedo(redoStackRef.current.length > 0)
	}, [])

	const pushUndo = useCallback((op: UndoableOperation) => {
		undoStackRef.current.push(op)
		// Clear redo stack on new action
		redoStackRef.current = []
		// Enforce limit
		if (undoStackRef.current.length > MAX_UNDO_LEVELS) {
			undoStackRef.current = undoStackRef.current.slice(
				undoStackRef.current.length - MAX_UNDO_LEVELS
			)
		}
		updateUndoState()
	}, [updateUndoState])

	const invalidateDocument = useCallback(() => {
		if (!documentId) return
		return queryClient.invalidateQueries({
			queryKey: getResolvedDocumentQueryKey(documentId),
		})
	}, [documentId, queryClient])

	// ========== Section Mutations ==========

	const createSection = useCallback(
		(data: CreateSectionData): Promise<SectionBlock> => {
			if (!documentId) return Promise.reject(new Error('No document ID provided'))

			return secureJsonFetch(`${baseUrl}/api/documents/${documentId}/sections`, {
				method: 'POST',
				body: data,
			}).then(async (response) => {
				if (!response.ok) {
					const error = await response.json().catch(() => ({ error: 'Failed to create section' }))
					throw new Error(error.error || 'Failed to create section')
				}

				const result = await response.json()
				const section = result.section as SectionBlock

				// Push undo operation (inverse = delete section)
				pushUndo({
					id: generateOperationId(),
					type: 'create_section',
					label: 'Create Section',
					inverseData: { sectionId: section.id },
					redoData: { documentId, data },
				})

				await invalidateDocument()
				return section
			})
		},
		[baseUrl, documentId, pushUndo, invalidateDocument]
	)

	const updateSection = useCallback(
		(sectionId: string, data: UpdateSectionData): Promise<SectionBlock> => {
			if (!documentId) return Promise.reject(new Error('No document ID provided'))

			const queryKey = getResolvedDocumentQueryKey(documentId)

			// Read original values from cache for undo
			const cachedData = queryClient.getQueryData<{ resolvedDocument: ResolvedDocument }>(queryKey)
			const originalSection = cachedData?.resolvedDocument?.children.find(
				(c) => c.type === 'section' && c.section.id === sectionId
			)
			const originalProps = originalSection?.type === 'section'
				? originalSection.section.properties
				: {}

			// Optimistic update
			queryClient.setQueryData(
				queryKey,
				(old: { resolvedDocument: ResolvedDocument; playbookId: string } | undefined) => {
					if (!old?.resolvedDocument) return old
					return {
						...old,
						resolvedDocument: {
							...old.resolvedDocument,
							children: old.resolvedDocument.children.map((child) => {
								if (child.type === 'section' && child.section.id === sectionId) {
									return {
										...child,
										section: {
											...child.section,
											properties: {
												...child.section.properties,
												...(data.name !== undefined && { name: data.name }),
												...(data.color !== undefined && { color: data.color }),
												...(data.collapsed !== undefined && { collapsed: data.collapsed }),
											},
										},
									}
								}
								return child
							}),
						},
					} as typeof old
				}
			)

			return secureJsonFetch(`${baseUrl}/api/sections/${sectionId}`, {
				method: 'PATCH',
				body: data,
			}).then(async (response) => {
				if (!response.ok) {
					// Rollback on error
					if (cachedData) {
						queryClient.setQueryData(queryKey, cachedData)
					}
					const error = await response.json().catch(() => ({ error: 'Failed to update section' }))
					throw new Error(error.error || 'Failed to update section')
				}

				// Push undo operation
				pushUndo({
					id: generateOperationId(),
					type: 'update_section',
					label: 'Update Section',
					inverseData: { sectionId, data: originalProps },
					redoData: { sectionId, data },
				})

				const result = await response.json()
				return result.section as SectionBlock
			}).catch((error) => {
				if (cachedData) {
					queryClient.setQueryData(queryKey, cachedData)
				}
				throw error
			})
		},
		[baseUrl, documentId, queryClient, pushUndo]
	)

	const deleteSection = useCallback(
		(sectionId: string): Promise<void> => {
			if (!documentId) return Promise.reject(new Error('No document ID provided'))

			const queryKey = getResolvedDocumentQueryKey(documentId)
			const previousData = queryClient.getQueryData<{ resolvedDocument: ResolvedDocument; playbookId: string }>(queryKey)

			// Optimistic delete
			queryClient.setQueryData(
				queryKey,
				(old: { resolvedDocument: ResolvedDocument; playbookId: string } | undefined) => {
					if (!old?.resolvedDocument) return old
					return {
						...old,
						resolvedDocument: {
							...old.resolvedDocument,
							children: old.resolvedDocument.children.filter((child) => {
								if (child.type === 'section') {
									return child.section.id !== sectionId
								}
								return true
							}),
						},
					} as typeof old
				}
			)

			return secureFetch(`${baseUrl}/api/sections/${sectionId}`, {
				method: 'DELETE',
			}).then(async (response) => {
				if (!response.ok) {
					if (previousData) {
						queryClient.setQueryData(queryKey, previousData)
					}
					const error = await response.json().catch(() => ({ error: 'Failed to delete section' }))
					throw new Error(error.error || 'Failed to delete section')
				}

				// Note: Delete operations are not undoable (would need to recreate all children)
				// Clear redo stack since we can't undo this
				redoStackRef.current = []
				updateUndoState()
			}).catch((error) => {
				if (previousData) {
					queryClient.setQueryData(queryKey, previousData)
				}
				throw error
			})
		},
		[baseUrl, documentId, queryClient, updateUndoState]
	)

	const updateDocument = useCallback(
		(data: UpdateDocumentData): Promise<DocumentBlock> => {
			if (!documentId) return Promise.reject(new Error('No document ID provided'))

			const queryKey = getResolvedDocumentQueryKey(documentId)
			const previousData = queryClient.getQueryData<{ resolvedDocument: ResolvedDocument; playbookId: string }>(queryKey)

			// Read original values from cache for undo
			const originalProps = previousData?.resolvedDocument?.document?.properties

			// Optimistic update
			queryClient.setQueryData(
				queryKey,
				(old: { resolvedDocument: ResolvedDocument; playbookId: string } | undefined) => {
					if (!old?.resolvedDocument) return old
					return {
						...old,
						resolvedDocument: {
							...old.resolvedDocument,
							document: {
								...old.resolvedDocument.document,
								properties: {
									...old.resolvedDocument.document.properties,
									...(data.name !== undefined && { name: data.name }),
									...(data.description !== undefined && { description: data.description }),
								},
							},
						},
					} as typeof old
				}
			)

			return secureJsonFetch(`${baseUrl}/api/documents/${documentId}`, {
				method: 'PATCH',
				body: data,
			}).then(async (response) => {
				if (!response.ok) {
					if (previousData) {
						queryClient.setQueryData(queryKey, previousData)
					}
					const error = await response.json().catch(() => ({ error: 'Failed to update document' }))
					throw new Error(error.error || 'Failed to update document')
				}

				// Push undo
				pushUndo({
					id: generateOperationId(),
					type: 'update_document',
					label: 'Update Document',
					inverseData: { data: originalProps },
					redoData: { data },
				})

				const result = await response.json()
				return result.document as DocumentBlock
			}).catch((error) => {
				if (previousData) {
					queryClient.setQueryData(queryKey, previousData)
				}
				throw error
			})
		},
		[baseUrl, documentId, queryClient, pushUndo]
	)

	// ========== Play Reference Mutations ==========

	const addPlayReference = useCallback(
		(sectionId: string, playId: string): Promise<PlayReferenceBlock> => {
			if (!documentId) return Promise.reject(new Error('No document ID provided'))

			return secureJsonFetch(`${baseUrl}/api/sections/${sectionId}/play-references`, {
				method: 'POST',
				body: { playId },
			}).then(async (response) => {
				if (!response.ok) {
					const error = await response.json().catch(() => ({ error: 'Failed to add play reference' }))
					throw new Error(error.error || 'Failed to add play reference')
				}

				const result = await response.json()
				const playReference = result.playReference as PlayReferenceBlock

				// Push undo (inverse = remove the created play reference)
				pushUndo({
					id: generateOperationId(),
					type: 'add_play_reference',
					label: 'Add Play',
					inverseData: { blockId: playReference.id },
					redoData: { sectionId, playId },
				})

				await invalidateDocument()
				return playReference
			})
		},
		[baseUrl, documentId, pushUndo, invalidateDocument]
	)

	const removePlayReference = useCallback(
		(blockId: string): Promise<void> => {
			if (!documentId) return Promise.reject(new Error('No document ID provided'))

			const queryKey = getResolvedDocumentQueryKey(documentId)
			const previousData = queryClient.getQueryData<{ resolvedDocument: ResolvedDocument; playbookId: string }>(queryKey)

			// Optimistic delete from cache
			queryClient.setQueryData(
				queryKey,
				(old: { resolvedDocument: ResolvedDocument; playbookId: string } | undefined) => {
					if (!old?.resolvedDocument) return old
					return {
						...old,
						resolvedDocument: {
							...old.resolvedDocument,
							children: old.resolvedDocument.children.map((child) => {
								if (child.type === 'section') {
									return {
										...child,
										children: child.children.filter((sc) => {
											if (sc.type === 'play_reference') {
												return sc.block.id !== blockId
											}
											return true
										}),
									}
								}
								return child
							}),
						},
					} as typeof old
				}
			)

			return secureFetch(`${baseUrl}/api/play-references/${blockId}`, {
				method: 'DELETE',
			}).then(async (response) => {
				if (!response.ok) {
					if (previousData) {
						queryClient.setQueryData(queryKey, previousData)
					}
					const error = await response.json().catch(() => ({ error: 'Failed to remove play reference' }))
					throw new Error(error.error || 'Failed to remove play reference')
				}

				// Note: Delete operations are not easily undoable (would need full block data)
				redoStackRef.current = []
				updateUndoState()
			}).catch((error) => {
				if (previousData) {
					queryClient.setQueryData(queryKey, previousData)
				}
				throw error
			})
		},
		[baseUrl, documentId, queryClient, updateUndoState]
	)

	// ========== Section Reordering ==========

	const reorderSections = useCallback(
		(sectionIds: string[]): Promise<void> => {
			if (!documentId) return Promise.reject(new Error('No document ID provided'))

			const queryKey = getResolvedDocumentQueryKey(documentId)
			const previousData = queryClient.getQueryData<{ resolvedDocument: ResolvedDocument; playbookId: string }>(queryKey)

			// Get current section order for undo
			const currentOrder = previousData?.resolvedDocument?.children
				.filter((c) => c.type === 'section')
				.map((c) => c.type === 'section' ? c.section.id : '')
				.filter(Boolean) ?? []

			// Optimistic reorder
			queryClient.setQueryData(
				queryKey,
				(old: { resolvedDocument: ResolvedDocument; playbookId: string } | undefined) => {
					if (!old?.resolvedDocument) return old
					const childrenBySectionId = new Map(
						old.resolvedDocument.children
							.filter((c) => c.type === 'section')
							.map((c) => [c.type === 'section' ? c.section.id : '', c])
					)
					const nonSectionChildren = old.resolvedDocument.children.filter((c) => c.type !== 'section')
					const reordered = sectionIds
						.map((id) => childrenBySectionId.get(id))
						.filter(Boolean)

					return {
						...old,
						resolvedDocument: {
							...old.resolvedDocument,
							children: [...reordered, ...nonSectionChildren],
						},
					} as typeof old
				}
			)

			return secureJsonFetch(`${baseUrl}/api/documents/${documentId}/sections/reorder`, {
				method: 'PUT',
				body: { sectionIds },
			}).then(async (response) => {
				if (!response.ok) {
					if (previousData) {
						queryClient.setQueryData(queryKey, previousData)
					}
					const error = await response.json().catch(() => ({ error: 'Failed to reorder sections' }))
					throw new Error(error.error || 'Failed to reorder sections')
				}

				// Push undo
				pushUndo({
					id: generateOperationId(),
					type: 'reorder_sections',
					label: 'Reorder Sections',
					inverseData: { sectionIds: currentOrder },
					redoData: { sectionIds },
				})
			}).catch((error) => {
				if (previousData) {
					queryClient.setQueryData(queryKey, previousData)
				}
				throw error
			})
		},
		[baseUrl, documentId, queryClient, pushUndo]
	)

	// ========== Undo/Redo ==========

	const executeOperation = useCallback(
		async (op: UndoableOperation, isUndo: boolean): Promise<void> => {
			const opData = isUndo ? op.inverseData : op.redoData

			switch (op.type) {
				case 'create_section': {
					if (isUndo) {
						// Undo create = delete
						const sectionId = opData.sectionId as string
						await secureFetch(`${baseUrl}/api/sections/${sectionId}`, { method: 'DELETE' })
					} else {
						// Redo create = create again
						const data = opData.data as CreateSectionData
						const docId = opData.documentId as string
						await secureJsonFetch(`${baseUrl}/api/documents/${docId}/sections`, {
							method: 'POST',
							body: data,
						})
					}
					break
				}
				case 'update_section': {
					const sectionId = opData.sectionId as string
					const data = opData.data as UpdateSectionData
					await secureJsonFetch(`${baseUrl}/api/sections/${sectionId}`, {
						method: 'PATCH',
						body: data,
					})
					break
				}
				case 'update_document': {
					if (!documentId) break
					const data = opData.data as UpdateDocumentData
					await secureJsonFetch(`${baseUrl}/api/documents/${documentId}`, {
						method: 'PATCH',
						body: data,
					})
					break
				}
				case 'add_play_reference': {
					if (isUndo) {
						// Undo add = remove
						const blockId = opData.blockId as string
						await secureFetch(`${baseUrl}/api/play-references/${blockId}`, { method: 'DELETE' })
					} else {
						// Redo add = add again
						const sectionId = opData.sectionId as string
						const playId = opData.playId as string
						await secureJsonFetch(`${baseUrl}/api/sections/${sectionId}/play-references`, {
							method: 'POST',
							body: { playId },
						})
					}
					break
				}
				case 'reorder_sections': {
					if (!documentId) break
					const sectionIds = opData.sectionIds as string[]
					await secureJsonFetch(`${baseUrl}/api/documents/${documentId}/sections/reorder`, {
						method: 'PUT',
						body: { sectionIds },
					})
					break
				}
				default:
					break
			}

			// Invalidate cache to refetch latest state
			await invalidateDocument()
		},
		[baseUrl, documentId, invalidateDocument]
	)

	const undo = useCallback(async (): Promise<void> => {
		const stack = undoStackRef.current
		if (stack.length === 0) return

		const op = stack.pop()
		if (!op) return

		try {
			await executeOperation(op, true)
			redoStackRef.current.push(op)
		} catch (error) {
			// Put it back on failure
			stack.push(op)
			console.error('Undo failed:', error)
		}

		updateUndoState()
	}, [executeOperation, updateUndoState])

	const redo = useCallback(async (): Promise<void> => {
		const stack = redoStackRef.current
		if (stack.length === 0) return

		const op = stack.pop()
		if (!op) return

		try {
			await executeOperation(op, false)
			undoStackRef.current.push(op)
		} catch (error) {
			// Put it back on failure
			stack.push(op)
			console.error('Redo failed:', error)
		}

		updateUndoState()
	}, [executeOperation, updateUndoState])

	return {
		createSection,
		updateSection,
		deleteSection,
		updateDocument,
		addPlayReference,
		removePlayReference,
		reorderSections,
		undo,
		redo,
		canUndo,
		canRedo,
	}
}
